import { newsList } from "@/mocks/news";
import { recommendList } from "@/mocks/recommend";
import { characterList } from "@/mocks/characters";
import type {
  NewsItem,
  RecommendItem,
  CharacterItem,
  CategoryType,
} from "@/types/content";

export type { NewsItem, RecommendItem, CharacterItem, CategoryType };

export interface SearchResult {
  type: CategoryType;
  id: string;
  title: string;
  summary: string;
  cover: string;
  meta: string;
  date: string;
}

export const categoryMeta: Record<
  CategoryType,
  { title: string; desc: string; icon: string; route: string }
> = {
  news: {
    title: "动漫资讯",
    desc: "追踪最新动漫动态、定档开播与行业热点，第一时间掌握你关心的动画消息。",
    icon: "ri-newspaper-line",
    route: "/category/news",
  },
  recommend: {
    title: "番剧推荐",
    desc: "精选热门与经典番剧，按类型、地区、年份分类，帮你快速找到下一部下饭好剧。",
    icon: "ri-movie-2-line",
    route: "/category/recommend",
  },
  character: {
    title: "角色介绍",
    desc: "深入了解人气角色的背景故事、配音阵容与相关作品，认识屏幕背后有血有肉的灵魂。",
    icon: "ri-user-star-line",
    route: "/category/character",
  },
};

export function isCategoryType(value: string): value is CategoryType {
  return value === "news" || value === "recommend" || value === "character";
}

export function getNewsById(id: string): NewsItem | undefined {
  return newsList.find((item) => item.id === id) as NewsItem | undefined;
}

export function getRecommendById(id: string): RecommendItem | undefined {
  return recommendList.find((item) => item.id === id) as RecommendItem | undefined;
}

export function getCharacterById(id: string): CharacterItem | undefined {
  return characterList.find((item) => item.id === id) as CharacterItem | undefined;
}

export function getCharactersByWorkId(workId: string): CharacterItem[] {
  return characterList.filter((item) => item.workId === workId) as CharacterItem[];
}

export function getRecommendByWorkId(workId: string): RecommendItem | undefined {
  return recommendList.find((item) => item.id === workId) as RecommendItem | undefined;
}

export function getSearchIndex(): SearchResult[] {
  const news = newsList.map((item) => ({
    type: "news" as CategoryType,
    id: item.id,
    title: item.title,
    summary: item.summary,
    cover: item.cover,
    meta: `${item.date} · ${item.source}`,
    date: item.date,
  }));
  const recommends = recommendList.map((item) => ({
    type: "recommend" as CategoryType,
    id: item.id,
    title: item.title,
    summary: item.summary,
    cover: item.cover,
    meta: `${item.region} · ${item.year} · ${item.status}`,
    date: "",
  }));
  const characters = characterList.map((item) => ({
    type: "character" as CategoryType,
    id: item.id,
    title: item.name,
    summary: item.intro,
    cover: item.image,
    meta: `《${item.work}》 · 配音：${item.voiceActor}`,
    date: "",
  }));
  return [...news, ...recommends, ...characters];
}

export function searchContent(keyword: string): SearchResult[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return getSearchIndex().filter((item) => {
    const haystack = `${item.title} ${item.summary} ${item.meta}`.toLowerCase();
    return haystack.includes(q);
  });
}

export const allTags: string[] = Array.from(
  new Set(recommendList.flatMap((item) => item.genres)),
);

export const allRegions: string[] = Array.from(
  new Set(recommendList.map((item) => item.region)),
);

export const allYears: string[] = Array.from(
  new Set(recommendList.map((item) => item.year)),
).sort((a, b) => b.localeCompare(a));